import { readThemePref, resolveTheme, type ThemePref } from "./theme";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type Density = "compact" | "regular" | "comfy";
export type SidebarMode = "expanded" | "condensed";

export type Tweaks = {
  /** "auto" follows the device; see ./theme. */
  themePref: ThemePref;
  density: Density;
  sidebar: SidebarMode;
  grid: boolean;
  accent: string;
  initialScreen: string;
};

export const TWEAK_DEFAULTS: Tweaks = {
  themePref: "auto",
  density: "comfy",
  sidebar: "expanded",
  grid: false,
  accent: "#FF7B1A",
  initialScreen: "dashboard",
};

const STORAGE_KEY = "ad:tweaks";

type TweaksContextValue = {
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
  reset: () => void;
};

const TweaksContext = createContext<TweaksContextValue | null>(null);

function loadFromStorage(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    delete parsed.theme; // legacy field, replaced by themePref
    return { ...TWEAK_DEFAULTS, ...parsed, themePref: readThemePref(raw) } as Tweaks;
  } catch {
    return TWEAK_DEFAULTS;
  }
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

export function TweaksProvider({ children }: { children: ReactNode }) {
  const [tweaks, setTweaks] = useState<Tweaks>(() => loadFromStorage());
  const [prefersDark, setPrefersDark] = useState(() => darkQuery()?.matches ?? false);

  // Follow device changes live while the preference is "auto".
  useEffect(() => {
    const mq = darkQuery();
    if (!mq) return;
    const onChange = () => setPrefersDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const setTweak = useCallback(
    <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => {
      setTweaks((prev) => {
        const next = { ...prev, [key]: value };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setTweaks(TWEAK_DEFAULTS);
  }, []);

  // Apply root attrs whenever tweaks change.
  useEffect(() => {
    const r = document.documentElement;
    r.setAttribute("data-theme", resolveTheme(tweaks.themePref, prefersDark));
    r.setAttribute("data-density", tweaks.density);
    r.setAttribute("data-sidebar", tweaks.sidebar);
    r.setAttribute("data-grid", tweaks.grid ? "on" : "off");
    r.style.setProperty("--rb-orange", tweaks.accent);

    const [R, G, B] = hexToRgb(tweaks.accent);
    r.style.setProperty("--ad-grid-color", `rgba(${R},${G},${B},0.40)`);
  }, [tweaks, prefersDark]);

  const value = useMemo<TweaksContextValue>(
    () => ({ tweaks, setTweak, reset }),
    [tweaks, setTweak, reset],
  );

  return <TweaksContext.Provider value={value}>{children}</TweaksContext.Provider>;
}

function darkQuery(): MediaQueryList | null {
  return typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;
}

export function useTweaks(): TweaksContextValue {
  const v = useContext(TweaksContext);
  if (!v) throw new Error("useTweaks must be used inside <TweaksProvider>");
  return v;
}
